import { nearestDestination, type FusedPosition } from "./position";
import { halifaxMonth, seasonCaption } from "./season";
import type { SceneryAirport } from "./types";

/** Inbox generation + assignment mode — not the Settings dispatchMode (inbox vs override). */
export type JobMode = "on_demand" | "automatic";

/** Spec default: Automatic assigns legal trips. Stored desk snapshots keep their own value. */
export const DEFAULT_JOB_MODE: JobMode = "automatic";

export function isJobMode(value: unknown): value is JobMode {
  return value === "on_demand" || value === "automatic";
}

export function hydrateJobMode(raw?: unknown): JobMode {
  return isJobMode(raw) ? raw : DEFAULT_JOB_MODE;
}

export const JOB_MODE_LABEL: Record<JobMode, string> = {
  on_demand: "On-demand",
  automatic: "Automatic",
};

/**
 * Ops / assignment nearest-field helper: Destinations airport closest to the
 * fused (or last-known) aircraft position. Inbox customer RFQs must use
 * `inboxOriginIcao` instead — last landing is not a charter origin.
 */
export function automaticOriginIcao(input: {
  position?: Pick<FusedPosition, "lat" | "lon"> | null;
  scenery: SceneryAirport[];
  homeIcao?: string;
  now?: Date;
  maxNm?: number;
}): { icao: string; reason: string; distanceNm?: number } {
  const month = halifaxMonth(input.now ?? new Date());
  const home = (input.homeIcao?.trim() || "").toUpperCase();
  if (input.position) {
    const near = nearestDestination(input.position, input.scenery, input.maxNm ?? 180);
    if (near) {
      return {
        icao: near.airport.icao,
        reason: `Near aircraft ${near.distanceNm.toFixed(0)} NM`,
        distanceNm: near.distanceNm,
      };
    }
  }
  const haveHome = input.scenery.some((s) => s.icao.toUpperCase() === home);
  const icao = haveHome ? home : (input.scenery[0]?.icao ?? home);
  const now = input.now ?? new Date();
  const caption = seasonCaption(month, now.getFullYear(), home ? [home] : []);
  return {
    icao,
    reason: input.position ? `No Destinations field within range — ${caption}` : `No live / last-known position — ${caption}`,
  };
}

/**
 * Customer RFQ origin hint: assigned airframe base, else company primary.
 * Never last landing, parked field, live nearest-airport, or a Celtic seasonal pad.
 */
export function inboxOriginIcao(input: {
  scenery: SceneryAirport[];
  homeIcao?: string;
  /** Assigned / candidate tail base — wins over company primary. */
  airframeBaseIcao?: string;
  now?: Date;
}): { icao: string; reason: string } {
  const now = input.now ?? new Date();
  const month = halifaxMonth(now);
  const assigned = (input.airframeBaseIcao ?? "").trim().toUpperCase();
  const companyHome = (input.homeIcao ?? "").trim().toUpperCase();
  const home = assigned || companyHome;
  const caption = seasonCaption(month, now.getFullYear(), companyHome ? [companyHome] : []);
  if (!home) {
    return { icao: "", reason: `No company home or tail base · ${caption}` };
  }
  const reason =
    assigned && home === assigned
      ? `Airframe base ${home} · ${caption}`
      : `Company home ${home} · ${caption}`;
  return { icao: home, reason };
}

export function jobModeHint(mode: JobMode, hasPosition: boolean): string {
  if (mode === "on_demand") {
    return "On-demand — Inbox stays an inbox. No silent auto-assign. Pilot / dispatcher accept or decline each trip.";
  }
  return hasPosition
    ? "Automatic — Inbox stays an inbox. Legal trips are assigned from aircraft state + duty. Pilot still accept / decline."
    : "Automatic — no live position yet. Last-known or parked field is used. Legal trips may still be assigned.";
}
